import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import { useAngularContext } from 'AngularContext';
import { useCurrentUser, useNavigationHelper } from 'FrontRoyalAngular';
import { hasAdminAccess } from 'Users';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { lessonEditorPath, lessonsEditorApi, streamEditorPath } from 'Lessons';
import { showToast } from 'Toast';
import {
    DuplicateContentItemDialog,
    type DuplicateOptions,
    type SavePublishState,
    type TranslatableExportSet,
} from 'ContentItemEditor';
import ConfirmDeleteCourseDialog from './ConfirmDeleteCourseDialog';
import ReviewKeyTermsDialog from './ReviewKeyTermsDialog';
import StaleVersionDialog from './StaleVersionDialog';
import StreamEditorForm from './StreamEditorForm';
import { isNotLoggedInError } from 'BackRoyalApi';
import { ERROR_TOAST_MS, messageFrom } from './serverErrors';
import { streamEditorApi } from './streamEditorApi';
import { entityMetadataFromForm, toForm } from './formFieldConversions';
import { useEditorCourses } from './useEditorCourses';
import { resourceFromAsset, summaryFromAsset, useStreamAssetUpload } from './useStreamAssetUpload';
import { useStreamRecord } from './useStreamRecord';
import { useStreamSave } from './useStreamSave';
import { streamEditorTheme } from './streamEditorTheme';
import { validationSchema } from './validationSchema';
import { buildStreamMetadataCsv } from './streamMetadataCsv';
import { type EmbeddedLesson, type StreamFormValues, type StreamMetadataCsv } from './StreamEditor.types';

const COURSES_LIST_ROUTE = '/editor?section=courses';

type ExportSetConstructor = new (
    lessonIds: string[],
    formats: string[],
    zipFilename: string,

    // The set calls getExportableMetadata() on this to put the metadata CSV in the zip. The
    // Angular page passed the Iguana stream, which carried the method on its prototype. A plain
    // record has no methods, so this page passes the method itself.
    stream: { getExportableMetadata: () => StreamMetadataCsv },
) => TranslatableExportSet;

// oxlint-disable-next-line max-lines-per-function -- the page owns the data and the actions of one screen
export default function StreamEditor({ streamId }: { streamId: string }) {
    const $injector = useAngularContext();
    const currentUser = useCurrentUser();
    const { loadRoute } = useNavigationHelper();
    const appHeaderViewModel = useAppHeaderViewModel();

    // The grey body background separates the cards from the page behind them.
    useEffect(() => {
        appHeaderViewModel.setBodyBackground();
        appHeaderViewModel.setTitleHTML('EDIT<br>COURSE');
        window.scrollTo(0, 0);
    }, [appHeaderViewModel]);

    const { data: loaded, isLoading, isFetching, error } = streamEditorApi.useGetStreamQuery(streamId);

    // The effect below reads the record through a ref, so a patch to the cached record does not
    // re-run it.
    const loadedRef = useRef(loaded);
    loadedRef.current = loaded;
    const loadedStreamId = loaded?.id;
    const { record, recordDirty, applyEdit, applySaved } = useStreamRecord(loaded);

    const { courses, coursesById, isLoading: coursesLoading } = useEditorCourses();
    const { data: lessonsData } = lessonsEditorApi.useGetLessonsForEditorListQuery(
        { locale: record?.locale || 'en' },
        { skip: !record },
    );

    const [uploadStreamImages] = streamEditorApi.useUploadStreamImagesMutation();
    const [uploadEntityMetadataImages] = streamEditorApi.useUploadEntityMetadataImagesMutation();
    const [destroyStream] = streamEditorApi.useDestroyStreamMutation();
    const [duplicateStream] = streamEditorApi.useDuplicateStreamMutation();
    const [updateEntityMetadata] = streamEditorApi.useUpdateEntityMetadataMutation();

    const [formReady, setFormReady] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [savingMetadata, setSavingMetadata] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [keyTermsOpen, setKeyTermsOpen] = useState(false);
    const [exportSet, setExportSet] = useState<TranslatableExportSet | null>(null);
    const [publishState, setPublishState] = useState<SavePublishState>({
        shouldPublish: false,
        shouldUnpublish: false,
    });

    const { upload, uploadingFor } = useStreamAssetUpload();

    const formFunctions = useForm<StreamFormValues>({
        mode: 'all',
        resolver: yupResolver(validationSchema),
        reValidateMode: 'onChange',
    });

    const { reset, resetField, getValues, setFieldValue } = formFunctions;

    // The loaded record is the form's baseline. Resetting to it again after a save is what clears
    // isDirty and makes the unsaved-changes warning quiet.
    //
    /*
        The form does not render until this has run. Rendering it against empty values first makes
        every select flip from uncontrolled to controlled, and shows the admin a blank form for a
        frame. useRecordFromAdminTable holds its form back for the same reason.

        It waits for isFetching. The cache keeps a course for 60 seconds, so re-entering the editor
        inside that window serves the old record on the first render while forceRefetch goes to the
        server. Resetting from that one leaves the form holding the previous visit's values while
        `record` takes the new updated_at, and a save then passes the optimistic lock and overwrites
        whatever another editor did in between.

        It runs once per course, not on every change to the cached record. A save patches that cache
        and does not refetch, so nothing here throws away what the admin has typed since.
    */
    useEffect(() => {
        if (!loadedStreamId || isFetching) return;

        reset(toForm(loadedRef.current!));
        setFormReady(true);
    }, [loadedStreamId, isFetching, reset]);

    const { save, saving, justSaved, staleVersion, dismissStaleVersion } = useStreamSave({
        record: record!,
        onSaved: saved => {
            applySaved(saved);
            reset(toForm(saved));

            // The Angular panel never cleared these, so a second save republished without being
            // asked to.
            setPublishState({ shouldPublish: false, shouldUnpublish: false });
        },
    });

    // A lesson already in the course comes from the record, which carries its key terms. A lesson
    // the admin may add comes from the editor list.
    const lessonsById = useMemo(() => {
        const fromList = Object.fromEntries(
            (lessonsData?.contents?.lessons || []).map(lesson => [lesson.id, lesson as EmbeddedLesson]),
        );
        const fromRecord = Object.fromEntries((record?.lessons || []).map(lesson => [lesson.id, lesson]));

        return { ...fromList, ...fromRecord };
    }, [lessonsData, record]);

    // An assessment may not be added once assessment editing is locked. The Angular page applied
    // the same filter to its lesson list.
    const availableLessons = useMemo(() => {
        const lessons = (lessonsData?.contents?.lessons || []) as EmbeddedLesson[];
        if (!record?.locale_pack?.assessment_editing_locked) return lessons;

        return lessons.filter(lesson => !lesson.assessment);
    }, [lessonsData, record]);

    if (isLoading || !record || !formReady) {
        if (error) {
            const status = (error as { status?: number }).status;

            // A 401 is either an expired session or a course this user may not read, and the two
            // need different instructions.
            const message =
                status === 404
                    ? 'That course could not be found.'
                    : status === 401
                      ? isNotLoggedInError(error)
                          ? 'Your session has expired. Sign in again to edit this course.'
                          : 'You do not have permission to edit this course.'
                      : 'The course could not be loaded. Reload the page to try again.';

            return <Alert severity="error">{message}</Alert>;
        }

        return <FrontRoyalSpinner />;
    }

    async function uploadImage(files: File[], target: 'course' | 'seo') {
        setUploadingImage(true);

        try {
            const assets =
                target === 'course'
                    ? await uploadStreamImages({ streamId, files }).unwrap()
                    : await uploadEntityMetadataImages({
                          entityMetadataId: record!.entity_metadata!.id,
                          files,
                      }).unwrap();

            const image = assets[0];
            if (!image) return;

            if (target === 'course') {
                applyEdit({ image });
            } else {
                applyEdit({ entity_metadata: { ...record!.entity_metadata!, image } });
            }
        } catch (uploadError) {
            showToast({
                message: 'The image was not uploaded',
                description: messageFrom(uploadError, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setUploadingImage(false);
        }
    }

    async function saveMetadata() {
        const entityMetadata = record!.entity_metadata;
        if (!entityMetadata) return;

        setSavingMetadata(true);

        try {
            const saved = await updateEntityMetadata({
                record: entityMetadataFromForm(getValues(), record!)!,
            }).unwrap();

            // The server holds these now, so they are the form's new baseline rather than an edit.
            // resetField one at a time, because a whole-form reset would drop the course edits the
            // admin has not saved yet.
            applyEdit({ entity_metadata: saved }, { dirties: false });
            resetField('seo_title', { defaultValue: saved.title || '' });
            resetField('seo_description', { defaultValue: saved.description || '' });
            resetField('seo_canonical_url', { defaultValue: saved.canonical_url || '' });
            resetField('tweet_template', { defaultValue: saved.tweet_template || '' });

            showToast({ message: 'Metadata has been updated!', type: 'success' });
        } catch (metadataError) {
            showToast({
                message: 'The metadata was not saved',
                description: messageFrom(metadataError, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setSavingMetadata(false);
        }
    }

    async function confirmDelete() {
        setDeleting(true);

        try {
            await destroyStream(streamId).unwrap();
            setConfirmDeleteOpen(false);
            loadRoute(COURSES_LIST_ROUTE);
        } catch (deleteError) {
            showToast({
                message: 'The course was not deleted',
                description: messageFrom(deleteError, 'A published course must be unpublished first.'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setDeleting(false);
        }
    }

    async function confirmDuplicate({ title, locale, isTranslation }: DuplicateOptions) {
        setDuplicating(true);

        try {
            const duplicated = await duplicateStream({
                title,
                locale: isTranslation ? locale : record!.locale,
                duplicateFromId: record!.id,
                duplicateFromUpdatedAt: record!.updated_at,
                inSameLocalePack: isTranslation,
            }).unwrap();

            setDuplicateOpen(false);
            loadRoute(streamEditorPath({ streamId: duplicated.id }));
        } catch (duplicateError) {
            showToast({
                message: 'The course was not duplicated',
                description: messageFrom(duplicateError, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setDuplicating(false);
        }
    }

    function exportAllLessons() {
        // TranslatableLessonExportSet stays in Angular: it builds each file by compiling an Angular
        // template.
        const ExportSet = $injector.get<ExportSetConstructor>('TranslatableLessonExportSet');
        const formats = $injector.get<string[]>('LESSON_TRANSLATION_EXPORT_FORMATS');
        const lessonIds = (record!.chapters || []).flatMap(chapter =>
            (chapter.lesson_hashes || []).map(hash => hash.lesson_id),
        );

        setExportSet(
            new ExportSet(lessonIds, formats, `${record!.title}.zip`, {
                // Read at zip time, from the form, so the CSV carries what the admin sees.
                getExportableMetadata: () => buildStreamMetadataCsv(getValues()),
            }),
        );
    }

    const titlesByLocale = Object.fromEntries(
        (record.locale_pack?.content_items || []).map(contentItem => [contentItem.locale, contentItem.title]),
    );

    return (
        <ThemeProvider theme={streamEditorTheme}>
            {/*
                The widths the Bootstrap `.container` gave this page before the conversion
                (oreo/global/bootstrap_compat.scss). Its breakpoints are the same three this theme
                declares, so the steps line up. Worth keeping rather than running full width: the
                playlist editor, the lesson editor and the editor index are still Angular inside that
                container, and a course editor the width of the monitor would jump against them.
            */}
            <Stack
                sx={{
                    width: '100%',
                    maxWidth: { sm: 750, md: 970, lg: 1170 },
                    marginInline: 'auto',
                    paddingInline: 2,
                    paddingBlock: 3,
                }}
            >
                <FormProvider {...formFunctions}>
                    <StreamEditorForm
                        record={record}
                        recordDirty={recordDirty}
                        courses={courses}
                        coursesById={coursesById}
                        coursesLoading={coursesLoading}
                        lessonsById={lessonsById}
                        availableLessons={availableLessons}
                        lessonsLoaded={!!lessonsData}
                        canPublish={hasAdminAccess(currentUser)}
                        saving={saving}
                        justSaved={justSaved}
                        deleting={deleting}
                        uploadingImage={uploadingImage}
                        uploadingFor={uploadingFor}
                        exportSet={exportSet}
                        publishState={publishState}
                        onPublishStateChange={setPublishState}
                        onSave={() =>
                            save(getValues(), {
                                should_publish: publishState.shouldPublish,
                                should_unpublish: publishState.shouldUnpublish,
                            })
                        }
                        onDelete={() => setConfirmDeleteOpen(true)}
                        onSaveMetadata={saveMetadata}
                        savingMetadata={savingMetadata}
                        onRecordEdit={applyEdit}
                        onUploadImage={files => uploadImage(files, 'course')}
                        onUploadSeoImage={files => uploadImage(files, 'seo')}
                        onUploadDownloads={async files => {
                            const assets = await upload(files, { field: 'resource_downloads' });
                            if (assets.length === 0) return;

                            setFieldValue('resource_downloads', [
                                ...getValues('resource_downloads'),
                                ...assets.map(resourceFromAsset),
                            ]);
                        }}
                        onUploadSummaries={async files => {
                            const assets = await upload(files, { field: 'summaries' });
                            if (assets.length === 0) return;

                            setFieldValue('summaries', [...getValues('summaries'), ...assets.map(summaryFromAsset)]);
                        }}
                        onReviewKeyTerms={() => setKeyTermsOpen(true)}
                        onDuplicate={() => setDuplicateOpen(true)}
                        onExportAllLessons={exportAllLessons}
                        onGoBack={() => loadRoute(COURSES_LIST_ROUTE)}
                    />
                </FormProvider>

                <ConfirmDeleteCourseDialog
                    open={confirmDeleteOpen}
                    courseTitle={record.title}
                    deleting={deleting}
                    onCancel={() => setConfirmDeleteOpen(false)}
                    onConfirm={confirmDelete}
                />

                <DuplicateContentItemDialog
                    /*
                        Remounted on each open, so the default title follows a rename and the locale
                        picker is re-derived from the course's current locale. The Angular directive
                        was built fresh per open by DialogModal.
                    */
                    key={String(duplicateOpen)}
                    open={duplicateOpen}
                    itemTitle={record.title}
                    itemLocale={record.locale}
                    titlesByLocale={titlesByLocale}
                    saving={duplicating}
                    onCancel={() => setDuplicateOpen(false)}
                    onConfirm={confirmDuplicate}
                />

                <ReviewKeyTermsDialog
                    open={keyTermsOpen}
                    chapters={getValues('chapters') || record.chapters}
                    lessonsById={lessonsById}
                    editorUrlForLesson={lessonId => lessonEditorPath({ lessonId })}
                    onClose={() => setKeyTermsOpen(false)}
                />

                <StaleVersionDialog
                    message={staleVersion}
                    onDismiss={dismissStaleVersion}
                    onReload={() => {
                        dismissStaleVersion();
                        window.location.reload();
                    }}
                />
            </Stack>
        </ThemeProvider>
    );
}
